import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { navLinks } from "@/utils/site";
import { hotSearchWords } from "@/mocks/site";

export default function SiteHeader() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  const isActive = (to: string) => {
    const base = to.split("#")[0];
    if (base === "/") return location.pathname === "/" && !location.hash;
    return location.pathname.startsWith(base);
  };

  return (
    <header className="sticky top-0 z-50">
      <div className="hidden bg-foreground-950 text-background-100 md:block">
        <div className="mx-auto flex h-8 max-w-[1240px] items-center gap-3 overflow-hidden px-4 md:px-6">
          <span className="flex shrink-0 items-center gap-1.5 text-xs text-secondary-300">
            <i className="ri-fire-line" />
            今日热议
          </span>
          <div className="flex flex-1 items-center gap-4 overflow-hidden">
            <div className="flex min-w-max animate-marquee items-center gap-4">
              {[...hotSearchWords, ...hotSearchWords].map((word, index) => (
                <Link
                  key={`${word}-${index}`}
                  to="/topics"
                  className="whitespace-nowrap text-xs text-background-300 transition-colors hover:text-secondary-300"
                >
                  # {word}
                </Link>
              ))}
            </div>
          </div>
          <span className="hidden shrink-0 items-center gap-1.5 text-xs text-background-400 lg:flex">
            <i className="ri-time-line" />
            每天 09:00–24:00 版主在线
          </span>
        </div>
      </div>

      <div
        className={`border-b transition-colors duration-300 ${
          scrolled
            ? "border-background-200 bg-background-50/95 backdrop-blur"
            : "border-transparent bg-background-50"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1240px] items-center gap-4 px-4 md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-xl text-background-50">
              <i className="ri-plant-fill" />
            </span>
            <span className="leading-none">
              <span className="block font-brand text-xl text-foreground-950">瓜田社区</span>
              <span className="mt-0.5 hidden text-[11px] text-foreground-500 sm:block">
                有瓜一起吃，有话大家聊
              </span>
            </span>
          </Link>

          <nav className="ml-6 hidden flex-1 items-center gap-1 md:flex">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`flex h-9 items-center rounded-md px-3 text-sm transition-colors ${
                  isActive(link.to)
                    ? "bg-primary-50 font-medium text-primary-700"
                    : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="ml-auto hidden items-center gap-2 md:flex">
            <Link
              to="/topics"
              className="flex h-9 items-center gap-1.5 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-search-line" />
              找话题
            </Link>
            <Link
              to="/topics"
              className="flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-quill-pen-line" />
              去发帖
            </Link>
          </div>

          <button
            type="button"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((value) => !value)}
            className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-xl text-foreground-800 md:hidden"
          >
            <i className={menuOpen ? "ri-close-line" : "ri-menu-2-line"} />
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
            <nav className="flex flex-col">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center justify-between rounded-md px-3 py-3 text-sm ${
                    isActive(link.to)
                      ? "bg-primary-50 font-medium text-primary-700"
                      : "text-foreground-700"
                  }`}
                >
                  {link.label}
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </Link>
              ))}
            </nav>
            <Link
              to="/topics"
              className="mt-2 flex h-11 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 text-sm font-medium text-background-50"
            >
              <i className="ri-quill-pen-line" />
              去发帖互动
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}